import { useState } from "react";
import { Instagram, Mail, MapPin, Phone, Send, Star } from "lucide-react";
import { FadeIn } from "@/components/FadeIn";
import { Ornament, Diamond } from "@/components/Ornament";
import { useLanguage } from "@/context/LanguageContext";
import { strings } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Boutique() {
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [contactStatus, setContactStatus] = useState("idle");
  const { lang } = useLanguage();
  const s = strings.boutique[lang];

  const submitContact = async (e) => {
    e.preventDefault();
    if (contactStatus === "sending") return;
    setContactStatus("sending");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: contact.name.trim(),
          email: contact.email.trim(),
          message: contact.message.trim(),
        }),
      });
      if (!res.ok) throw new Error();
      setContactStatus("sent");
      setContact({ name: "", email: "", message: "" });
    } catch {
      setContactStatus("error");
    }
  };

  return (
    <div data-testid="boutique-page">
      <section className="mx-auto max-w-6xl px-6 pt-16 md:px-8 md:pt-24">
        <FadeIn>
          <div className="text-center">
            <p className="eyebrow" data-testid="boutique-eyebrow">
              {s.eyebrow}
            </p>
            <h1 className="mt-6 font-serif text-4xl font-medium tracking-[0.12em] text-noir sm:text-5xl md:text-6xl" data-testid="boutique-title">
              {s.title}
            </h1>
            <div className="mt-10">
              <Ornament />
            </div>
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="mx-auto mt-14 max-w-3xl">
            <div className="double-frame" data-testid="boutique-facade-frame">
              <div className="double-frame-inner">
                <img
                  src="/livret/facade-reelle.webp"
                  alt="La façade de la boutique Roganel, 23 rue des Archives, Paris"
                  data-testid="boutique-facade-image"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </FadeIn>

        <div className="grid gap-14 py-16 md:grid-cols-2 md:gap-20 md:py-24">
          <FadeIn>
            <div data-testid="boutique-coordonnees">
              <p className="font-serif text-3xl font-medium tracking-[0.2em] text-bordeaux md:text-4xl" data-testid="boutique-nom">
                ROGANEL
              </p>
              <p className="mt-6 font-serif text-2xl leading-snug text-noir md:text-3xl" data-testid="boutique-adresse">
                23 rue des Archives
                <br />
                75004 Paris
              </p>
              <div className="mt-10 space-y-5 font-sans text-sm font-light tracking-wide text-noir/75">
                <a
                  href="tel:+33140290220"
                  data-testid="boutique-phone-link"
                  className="flex items-center gap-4 transition-colors duration-300 hover:text-bordeaux"
                >
                  <Phone size={16} strokeWidth={1.5} className="text-bordeaux" />
                  {s.phone}
                </a>
                <a
                  href="mailto:info@roganel.fr"
                  data-testid="boutique-email-link"
                  className="flex items-center gap-4 transition-colors duration-300 hover:text-bordeaux"
                >
                  <Mail size={16} strokeWidth={1.5} className="text-bordeaux" />
                  info@roganel.fr
                </a>
                <a
                  href="https://www.instagram.com/roganel75004"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="boutique-instagram-link"
                  className="flex items-center gap-4 transition-colors duration-300 hover:text-bordeaux"
                >
                  <Instagram size={16} strokeWidth={1.5} className="text-bordeaux" />
                  @roganel75004
                </a>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Roganel+23+rue+des+Archives+75004+Paris"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="boutique-itineraire-link"
                  className="flex items-center gap-4 transition-colors duration-300 hover:text-bordeaux"
                >
                  <MapPin size={16} strokeWidth={1.5} className="text-bordeaux" />
                  {s.directions}
                </a>
              </div>
              <div className="mt-10">
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Roganel+23+rue+des+Archives+75004+Paris"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline"
                  data-testid="boutique-avis-google-button"
                >
                  <Star size={14} strokeWidth={1.5} />
                  {s.reviews}
                </a>
              </div>
              <p className="mt-10 font-sans text-[11px] uppercase tracking-[0.3em] text-noir/40" data-testid="boutique-mention">
                {s.mention}
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.15}>
            <div data-testid="boutique-horaires">
              <p className="font-sans text-[11px] font-medium uppercase tracking-[0.3em] text-bordeaux" data-testid="boutique-horaires-title">
                {s.hoursTitle}
              </p>
              <div className="mt-8 divide-y divide-bordeaux/15 border-y border-bordeaux/15">
                {s.hours.map((h) => (
                  <div key={h.key} className="flex items-center justify-between py-5" data-testid={`boutique-horaire-${h.key}`}>
                    <span className="font-sans text-sm font-light tracking-wide text-noir/75">{h.day}</span>
                    <span className="font-serif text-lg text-noir">{h.time}</span>
                  </div>
                ))}
              </div>
              <div className="mt-12 flex items-start gap-4">
                <Diamond className="mt-2 shrink-0" />
                <p className="mx-auto max-w-2xl font-serif text-lg italic leading-relaxed text-noir/75 [text-wrap:pretty] md:text-xl" data-testid="boutique-philosophie">
                  {s.quote}
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
        <FadeIn>
          <div className="border-t border-bordeaux/10 pb-16 md:pb-24" data-testid="boutique-contact-section">
            <div className="mx-auto max-w-2xl pt-16 text-center md:pt-20">
              <h2 className="font-serif text-3xl font-medium tracking-[0.12em] text-noir md:text-4xl" data-testid="boutique-contact-title">
                {s.contactTitle}
              </h2>
              <div className="mt-8">
                <Ornament />
              </div>
              <p className="mt-8 font-sans text-sm font-light leading-loose text-noir/70 [text-wrap:pretty]" data-testid="boutique-contact-intro">
                {s.contactIntro}
              </p>
              {contactStatus === "sent" ? (
                <div className="mt-12" data-testid="boutique-contact-confirmation">
                  <p className="font-serif text-2xl italic text-bordeaux">
                    {s.confirmTitle}
                  </p>
                  <p className="mt-4 font-sans text-sm font-light leading-relaxed text-noir/70">
                    {s.confirmText}
                  </p>
                  <button
                    type="button"
                    onClick={() => setContactStatus("idle")}
                    className="btn-outline mt-10"
                    data-testid="boutique-contact-new-button"
                  >
                    {s.another}
                  </button>
                </div>
              ) : (
                <form onSubmit={submitContact} className="mt-12 text-left" data-testid="boutique-contact-form">
                  <label htmlFor="contact-name" className="eyebrow block">
                    {s.nameLabel}
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    required
                    maxLength={80}
                    value={contact.name}
                    onChange={(e) => setContact({ ...contact, name: e.target.value })}
                    data-testid="boutique-contact-name-input"
                    className="mt-3 w-full border border-bordeaux/30 bg-transparent px-4 py-3.5 font-sans text-sm font-light text-noir outline-none transition-colors duration-300 placeholder:text-noir/30 focus:border-bordeaux"
                    placeholder={s.namePlaceholder}
                  />
                  <label htmlFor="contact-email" className="eyebrow mt-8 block">
                    {s.emailLabel}
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    required
                    maxLength={120}
                    value={contact.email}
                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    data-testid="boutique-contact-email-input"
                    className="mt-3 w-full border border-bordeaux/30 bg-transparent px-4 py-3.5 font-sans text-sm font-light text-noir outline-none transition-colors duration-300 placeholder:text-noir/30 focus:border-bordeaux"
                    placeholder={s.emailPlaceholder}
                  />
                  <label htmlFor="contact-message" className="eyebrow mt-8 block">
                    {s.messageLabel}
                  </label>
                  <textarea
                    id="contact-message"
                    required
                    rows={5}
                    maxLength={2000}
                    value={contact.message}
                    onChange={(e) => setContact({ ...contact, message: e.target.value })}
                    data-testid="boutique-contact-message-input"
                    className="mt-3 w-full resize-none border border-bordeaux/30 bg-transparent px-4 py-3.5 font-sans text-sm font-light leading-relaxed text-noir outline-none transition-colors duration-300 placeholder:text-noir/30 focus:border-bordeaux"
                    placeholder={s.messagePlaceholder}
                  />
                  {contactStatus === "error" && (
                    <p className="mt-4 text-center font-sans text-xs text-bordeaux" data-testid="boutique-contact-error">
                      {s.error}
                    </p>
                  )}
                  <div className="mt-10 text-center">
                    <button type="submit" className="btn-primary" disabled={contactStatus === "sending"} data-testid="boutique-contact-submit-button">
                      <Send size={14} strokeWidth={1.5} />
                      {contactStatus === "sending" ? s.sending : s.send}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </FadeIn>
      </section>
    </div>
  );
}
